import { filter, find, findIndex, findLast, isEmpty, max, omit } from 'lodash';
import { action, makeObservable, observable } from 'mobx';
import { v4 as uuidv4 } from 'uuid';

import { EngineTab } from '@/constants';
import { UnitMeasurementSystem } from '@/constants/enums';

import { EngineStepsEnum, rangeEditableFields } from './constants';
import {
  convertFlowMinToImperial,
  convertFlowMinToMetric,
  convertNullToInfinity,
  convertPressureToImperial,
  convertPressureToMetric,
} from './utils';

export class MainRangesConfiguration {
  @observable unit: UnitMeasurementSystem;
  @observable flowLevels: FlowLevels;
  @observable pressureLevels: PressureLevels;

  constructor(mainRangesConfiguration?, tvalue?, unitMeasurementSystem?) {
    makeObservable(this);
    this.unit = mainRangesConfiguration?.unit ?? UnitMeasurementSystem.Imperial;
    this.flowLevels = new FlowLevels(mainRangesConfiguration?.flowLevels, tvalue, unitMeasurementSystem);
    this.pressureLevels = new PressureLevels(
      mainRangesConfiguration?.flowLevels,
      mainRangesConfiguration?.pressureLevels,
      tvalue,
      unitMeasurementSystem,
    );
  }

  @action setUnit(unit: UnitMeasurementSystem) {
    this.unit = unit;
  }

  getValuesToSave(tvalue, unitMeasurementSystem) {
    return {
      flowLevels: this.flowLevels.getValuesToSave(tvalue, unitMeasurementSystem),
      pressureLevels: this.pressureLevels.getValuesToSave(tvalue, unitMeasurementSystem),
      unit: this.unit,
    };
  }

  hasChanges() {
    return (
      this.flowLevels?.hasChanges() ||
      this.pressureLevels?.hasChanges() ||
      this.unit !== JSON.parse(window.localStorage.getItem(EngineStepsEnum.MainRangesConfiguration))?.unit
    );
  }

  cancelChanges() {
    this.flowLevels?.cancelChanges();
    this.pressureLevels?.cancelChanges();
    this.unit = JSON.parse(window.localStorage.getItem(EngineStepsEnum.MainRangesConfiguration))?.unit;
  }
}

export class Levels {
  @observable ranges: Range[] = [];

  @action addRange(parentRangeKey?, parentRangesCount?) {
    const rangesLength = parentRangeKey ? filter(this.ranges, { parentRangeKey })?.length : this.ranges?.length;
    const lastRange = parentRangeKey ? findLast(this.ranges, { parentRangeKey }) : this.ranges?.[rangesLength - 1];
    this.ranges = [
      // set previous range upperBound to null instead of Infinity
      ...this.ranges.map(range =>
        range?.key === lastRange?.key && lastRange?.upperBound === Infinity
          ? new Range({ ...lastRange, upperBound: null })
          : range,
      ),
      new Range({
        key: uuidv4(),
        lowerBound: rangesLength
          ? lastRange?.upperBound && lastRange?.upperBound !== Infinity
            ? lastRange?.upperBound
            : null
          : 0,
        upperBound: rangesLength ? Infinity : null,
        name: `${parentRangeKey ? 'Range' : 'Level'}${this.tableNameCounter(parentRangesCount) + 1}`,
        ...(parentRangeKey && { parentRangeKey }),
      }),
    ];
  }

  @action setRangeField(key, fieldKey, fieldValue, parentRangeKey?) {
    const ranges = parentRangeKey ? filter(this.ranges, { parentRangeKey }) : this.ranges;
    const currentRangeIndex = findIndex(ranges, { key });
    const nextRange = ranges?.[currentRangeIndex + 1];
    this.ranges = this.ranges.map(range => {
      if (range?.key === nextRange?.key && fieldKey === 'upperBound')
        // update next lowerBound when updating current upperBound
        return new Range({ ...range, lowerBound: fieldValue });
      else if (range.key === key) return new Range({ ...range, [fieldKey]: fieldValue });
      else return range;
    });
  }

  @action deleteRange(key, parentRangeKey?) {
    const ranges = parentRangeKey ? filter(this.ranges, { parentRangeKey }) : this.ranges;
    const currentRangeIndex = findIndex(ranges, { key });
    const previousRange = ranges?.[currentRangeIndex - 1];
    const nextRange = ranges?.[currentRangeIndex + 1];
    const rangesLength = ranges?.length;

    this.ranges = this.ranges.reduce((result, current) => {
      let currentResult;
      // delete last one => previous upperBound becomes Infinity
      if (currentRangeIndex === rangesLength - 1 && current?.key === previousRange?.key)
        currentResult = new Range({ ...current, upperBound: Infinity });
      // delete middle => next lowerBound becomes previous upperBound
      else if (current?.key === nextRange?.key)
        currentResult = new Range({ ...current, lowerBound: previousRange?.upperBound });
      else if (current.key !== key) currentResult = current;
      return [...result, ...(currentResult ? [currentResult] : [])];
    }, []);
  }

  validate(parentRangeKey?, focusedField?) {
    const ranges = parentRangeKey ? filter(this.ranges, { parentRangeKey }) : this.ranges;
    return ranges?.reduce((result, current, index) => {
      const validationMessages = [];
      if (!current?.upperBound)
        validationMessages.push({
          key: current?.key,
          field: 'upperBound',
          message: 'rangesAreMandatory',
          focused: focusedField ? focusedField === `upperBound-${current?.key}` : true,
          tab: EngineTab.MainRangesConfiguration,
        });
      if (
        current?.upperBound !== Infinity &&
        ranges?.[index - 1]?.upperBound &&
        +current?.upperBound <= +ranges?.[index - 1]?.upperBound
      )
        validationMessages.push({
          key: current?.key,
          field: 'upperBound',
          message: 'higherUpperBound',
          focused: focusedField ? focusedField === `upperBound-${current?.key}` : true,
          tab: EngineTab.MainRangesConfiguration,
        });
      return [...result, ...(validationMessages.length ? validationMessages : [])];
    }, []);
  }

  getRangeByKey(rangeId) {
    return find(this.ranges, { key: rangeId });
  }

  convertRangesToUnitMeasurementSystem(ranges, tvalue, unitMeasurementSystem, converterFunc) {
    return unitMeasurementSystem === UnitMeasurementSystem.Imperial
      ? ranges?.map(range => ({
          ...range,
          lowerBound: converterFunc(range?.lowerBound, tvalue),
          upperBound: converterFunc(range?.upperBound, tvalue),
        }))
      : ranges;
  }

  tableNameCounter(flowLevelsCount?) {
    const firstFlowTableNameCounters = (
      (flowLevelsCount ? this.ranges?.slice(0, flowLevelsCount) : this.ranges) ?? []
    ).map(range => range?.name?.substring(5));
    return +(max(firstFlowTableNameCounters) ?? 0);
  }
}

export class FlowLevels extends Levels {
  @observable oldValues;

  constructor(flowLevels?, tvalue?, unitMeasurementSystem?) {
    super();
    makeObservable(this);
    const ranges = super.convertRangesToUnitMeasurementSystem(
      flowLevels?.ranges,
      tvalue,
      unitMeasurementSystem,
      convertFlowMinToImperial,
    );
    this.ranges = ranges ?? [
      new Range({
        key: uuidv4(),
        lowerBound: 0,
        upperBound: Infinity,
        name: `Level${this.tableNameCounter() + 1}`,
      }),
    ];
    const mainRangesConfiguration = window.localStorage.getItem(EngineStepsEnum.MainRangesConfiguration);
    if (mainRangesConfiguration) {
      const parsedMainRangesConfiguration = JSON.parse(mainRangesConfiguration, convertNullToInfinity);
      this.oldValues = {
        ranges: super.convertRangesToUnitMeasurementSystem(
          parsedMainRangesConfiguration?.flowLevels?.ranges,
          tvalue,
          unitMeasurementSystem,
          convertFlowMinToImperial,
        ),
      };
    }
  }

  hasChanges() {
    return (
      !isEmpty(this.oldValues) &&
      rangeEditableFields.some(field =>
        this.ranges?.some(range => range?.[field] !== find(this.oldValues?.ranges, { key: range.key })?.[field]),
      )
    );
  }

  @action cancelChanges() {
    this.ranges = this.oldValues?.ranges;
  }

  getValuesToSave(tvalue, unitMeasurementSystem) {
    const ranges = super.convertRangesToUnitMeasurementSystem(
      this.ranges,
      tvalue,
      unitMeasurementSystem,
      convertFlowMinToMetric,
    );
    return omit({ ...this, ranges }, 'oldValues');
  }

  getOldRangeValues(key) {
    return find(this.oldValues?.ranges, { key });
  }
}

export class PressureLevels extends Levels {
  @observable oldValues: Partial<PressureLevels>;

  constructor(flowLevels, pressureLevels?, tvalue?, unitMeasurementSystem?) {
    super();
    makeObservable(this);
    flowLevels?.ranges?.forEach((flowRange, flowIndex) => {
      const pressureRangesByFlow = filter(pressureLevels.ranges, { parentRangeKey: flowRange?.key });
      const ranges = super.convertRangesToUnitMeasurementSystem(
        pressureRangesByFlow,
        tvalue,
        unitMeasurementSystem,
        convertPressureToImperial,
      );
      if (ranges?.length) this.ranges.push(...ranges);
      else if (flowIndex === 0)
        this.ranges.push(
          new Range({
            key: uuidv4(),
            name: `Range${this.tableNameCounter(flowLevels?.ranges?.length) + 1}`,
            lowerBound: 0,
            upperBound: Infinity,
            parentRangeKey: flowRange.key,
          }),
        );
    });
    const mainRangesConfiguration = window.localStorage.getItem(EngineStepsEnum.MainRangesConfiguration);
    if (mainRangesConfiguration)
      this.oldValues = {
        ranges: super.convertRangesToUnitMeasurementSystem(
          JSON.parse(mainRangesConfiguration, convertNullToInfinity)?.pressureLevels?.ranges,
          tvalue,
          unitMeasurementSystem,
          convertPressureToImperial,
        ),
      };
  }

  getPressureRangesByFlow(flowRangeId) {
    return filter(this.ranges, { parentRangeKey: flowRangeId });
  }

  hasChanges() {
    return (
      !isEmpty(this.oldValues) &&
      rangeEditableFields.some(field =>
        this.ranges?.some(range => range?.[field] !== find(this.oldValues?.ranges, { key: range.key })?.[field]),
      )
    );
  }

  @action cancelChanges() {
    this.ranges = this.oldValues?.ranges;
  }

  getValuesToSave(tvalue, unitMeasurementSystem) {
    const ranges = super.convertRangesToUnitMeasurementSystem(
      this.ranges,
      tvalue,
      unitMeasurementSystem,
      convertPressureToMetric,
    );
    return omit({ ...this, ranges }, 'oldValues');
  }

  getOldRangeValues(key) {
    return find(this.oldValues?.ranges, { key });
  }

  @action copyPressureRanges(flowLevels) {
    const firstFlowPressureRanges = this.ranges.filter(
      pressureRange => pressureRange?.parentRangeKey === flowLevels?.[0]?.key,
    );
    const oldPressureRangesByFlow = flowLevels.map(flowLevel => this.getPressureRangesByFlow(flowLevel?.key));
    this.ranges = [];
    flowLevels.forEach((flowRange, flowIndex) =>
      firstFlowPressureRanges?.forEach((pressureRange, pressureIndex) =>
        this.ranges.push(
          new Range({
            ...pressureRange,
            key: oldPressureRangesByFlow[flowIndex]?.[pressureIndex]?.key ?? uuidv4(),
            parentRangeKey: flowRange?.key,
            name: `Range${firstFlowPressureRanges?.length * flowIndex + pressureIndex + 1}`,
          }),
        ),
      ),
    );
  }
}

export class Range {
  @observable key: string;
  @observable name: string;
  @observable lowerBound: number;
  @observable upperBound: number;
  @observable parentRangeKey: string;

  constructor(range) {
    makeObservable(this);
    this.key = range?.key;
    this.name = range?.name;
    this.lowerBound = range?.lowerBound;
    this.upperBound = range?.upperBound;
    this.parentRangeKey = range?.parentRangeKey;
  }

  hasChanges(oldRange) {
    return !isEmpty(oldRange) && rangeEditableFields.some(field => this?.[field] !== oldRange?.[field]);
  }
}
